import React, { useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Popconfirm,
  message,
  Typography,
  Tag,
  Card,
  Tabs,
  Row,
  Col,
  InputNumber,
  TimePicker,
     // Image removed - not used
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
     // EyeOutlined removed - not used
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { storesApi } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface OperatingHours {
  monday: { open: string; close: string; isClosed: boolean };
  tuesday: { open: string; close: string; isClosed: boolean };
  wednesday: { open: string; close: string; isClosed: boolean };
  thursday: { open: string; close: string; isClosed: boolean };
  friday: { open: string; close: string; isClosed: boolean };
  saturday: { open: string; close: string; isClosed: boolean };
  sunday: { open: string; close: string; isClosed: boolean };
}

interface Store {
  _id: string;
  name: { en: string; zh: string };
  address: { en: string; zh: string };
  region: { en: string; zh: string };
  district?: { en: string; zh: string };
  location: {
    type: string;
    coordinates: [number, number]; // [longitude, latitude]
  };
  phone: string;
  email?: string;
  website?: string;
  operatingHours: OperatingHours;
  specialHours: Array<{
    date: string;
    note: { en: string; zh: string };
    isClosed: boolean;
    open?: string;
    close?: string;
  }>;
  services: Array<{ en: string; zh: string }>;
  features: Array<{ en: string; zh: string }>;
  images: Array<{
    url: string;
    alt: { en: string; zh: string };
    isPrimary: boolean;
  }>;
  description?: { en: string; zh: string };
  isActive: boolean;
  sortOrder: number;
  manager?: {
    name: string;
    phone: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

const AdminStores: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch stores
  const { data: storesData, isLoading } = useQuery({
    queryKey: ['admin-stores'],
    queryFn: () => storesApi.getAll({ admin: true })
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: storesApi.create,
    onSuccess: () => {
      message.success('Store created successfully');
      setIsModalVisible(false);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['admin-stores'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create store');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => storesApi.update(id, data),
    onSuccess: () => {
      message.success('Store updated successfully');
      setIsModalVisible(false);
      form.resetFields();
      setEditingStore(null);
      queryClient.invalidateQueries({ queryKey: ['admin-stores'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update store');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: storesApi.delete,
    onSuccess: () => {
      message.success('Store deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-stores'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete store');
    }
  });

  const handleAdd = () => {
    setEditingStore(null);
    form.resetFields();
    // Set default operating hours with dayjs objects
    const defaultHours = {
      monday: { open: dayjs('09:00', 'HH:mm'), close: dayjs('18:00', 'HH:mm'), isClosed: false },
      tuesday: { open: dayjs('09:00', 'HH:mm'), close: dayjs('18:00', 'HH:mm'), isClosed: false },
      wednesday: { open: dayjs('09:00', 'HH:mm'), close: dayjs('18:00', 'HH:mm'), isClosed: false },
      thursday: { open: dayjs('09:00', 'HH:mm'), close: dayjs('18:00', 'HH:mm'), isClosed: false },
      friday: { open: dayjs('09:00', 'HH:mm'), close: dayjs('18:00', 'HH:mm'), isClosed: false },
      saturday: { open: dayjs('09:00', 'HH:mm'), close: dayjs('18:00', 'HH:mm'), isClosed: false },
      sunday: { open: dayjs('09:00', 'HH:mm'), close: dayjs('18:00', 'HH:mm'), isClosed: true }
    };
    form.setFieldsValue({ operatingHours: defaultHours });
    setIsModalVisible(true);
  };

  const handleEdit = (store: Store) => {
    setEditingStore(store);
    
    // Convert time strings to dayjs objects for TimePicker
    const operatingHours: any = {};
    Object.keys(store.operatingHours || {}).forEach(day => {
      const hours = store.operatingHours[day as keyof OperatingHours];
      operatingHours[day] = {
        ...hours,
        open: hours?.open && hours.open !== '' ? dayjs(hours.open, 'HH:mm') : null,
        close: hours?.close && hours.close !== '' ? dayjs(hours.close, 'HH:mm') : null
      };
    });

    form.setFieldsValue({
      name: store.name || { en: '', zh: '' },
      address: store.address || { en: '', zh: '' },
      region: store.region || { en: '', zh: '' },
      district: store.district || { en: '', zh: '' },
      location: {
        type: 'Point',
        coordinates: store.location?.coordinates || [0, 0]
      },
      phone: store.phone || '',
      email: store.email || '',
      website: store.website || '',
      operatingHours,
      services: store.services || [],
      features: store.features || [],
      description: store.description || { en: '', zh: '' },
      isActive: store.isActive !== undefined ? store.isActive : true,
      sortOrder: store.sortOrder || 0,
      manager: store.manager || { name: '', phone: '', email: '' }
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Convert dayjs objects back to time strings
      const operatingHours: any = {};
      Object.keys(values.operatingHours || {}).forEach(day => {
        const hours = values.operatingHours[day];
        operatingHours[day] = {
          ...hours,
          open: hours?.open && dayjs.isDayjs(hours.open) ? hours.open.format('HH:mm') : '',
          close: hours?.close && dayjs.isDayjs(hours.close) ? hours.close.format('HH:mm') : ''
        };
      });

      const submitData = {
        ...values,
        operatingHours,
        location: {
          type: 'Point',
          coordinates: values.location?.coordinates || [0, 0]
        }
      };
      
      if (editingStore) {
        updateMutation.mutate({ id: editingStore._id, data: submitData });
      } else {
        createMutation.mutate(submitData);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const formatCoordinates = (coordinates: [number, number]) => {
    if (!coordinates || coordinates.length !== 2) return 'No location';
    return `${coordinates[1].toFixed(6)}, ${coordinates[0].toFixed(6)}`;
  };

  const columns = [
    {
      title: 'Store Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: { en: string; zh: string }) => (
        <div>
          <div className="font-medium">{name.en}</div>
          <div className="text-sm text-gray-500">{name.zh}</div>
        </div>
      )
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      render: (region: { en: string; zh: string }) => (
        <Tag color="blue">{region.en}</Tag>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_: any, record: Store) => (
        <Space direction="vertical" size="small">
          <Text type="secondary">
            <PhoneOutlined /> {record.phone}
          </Text>
          {record.email && (
            <Text type="secondary">
              <MailOutlined /> {record.email}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Location',
      key: 'location',
      render: (_: any, record: Store) => (
        <div>
          <div className="text-sm">{record.address.en}</div>
          <Text type="secondary" className="text-xs">
            <EnvironmentOutlined /> {formatCoordinates(record.location.coordinates)}
          </Text>
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: Store) => (
        <Space direction="vertical" size="small">
          <Tag color={record.isActive ? 'green' : 'red'}>
            {record.isActive ? 'Active' : 'Inactive'}
          </Tag>
          <Text type="secondary" className="text-xs">
            <ClockCircleOutlined /> Order: {record.sortOrder}
          </Text>
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Store) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            type="text"
          />
          <Popconfirm
            title="Are you sure you want to delete this store?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} type="text" danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>Stores Management</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          size="large"
        >
          Add Store
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={storesData?.stores || []}
          loading={isLoading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} stores`
          }}
        />
      </Card>

      <Modal
        title={editingStore ? 'Edit Store' : 'Add New Store'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={1000}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            isActive: true,
            sortOrder: 0,
            services: [],
            features: []
          }}
        >
          <Tabs>
            <Tabs.TabPane tab="Basic Info" key="basic">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['name', 'en']}
                    label="Store Name (English)"
                    rules={[{ required: true, message: 'Please enter English store name' }]}
                  >
                    <Input placeholder="Enter store name in English" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['name', 'zh']}
                    label="Store Name (Chinese)"
                    rules={[{ required: true, message: 'Please enter Chinese store name' }]}
                  >
                    <Input placeholder="輸入中文店鋪名稱" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['address', 'en']}
                    label="Address (English)"
                    rules={[{ required: true, message: 'Please enter English address' }]}
                  >
                    <TextArea rows={2} placeholder="Full address in English" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['address', 'zh']}
                    label="Address (Chinese)"
                    rules={[{ required: true, message: 'Please enter Chinese address' }]}
                  >
                    <TextArea rows={2} placeholder="中文地址" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['region', 'en']}
                    label="Region (English)"
                    rules={[{ required: true, message: 'Please enter English region' }]}
                  >
                    <Select placeholder="Select region">
                      <Option value="Hong Kong Island">Hong Kong Island</Option>
                      <Option value="Kowloon">Kowloon</Option>
                      <Option value="New Territories">New Territories</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['region', 'zh']}
                    label="Region (Chinese)"
                    rules={[{ required: true, message: 'Please enter Chinese region' }]}
                  >
                    <Select placeholder="選擇地區">
                      <Option value="香港島">香港島</Option>
                      <Option value="九龍">九龍</Option>
                      <Option value="新界">新界</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="phone"
                    label="Phone Number"
                    rules={[{ required: true, message: 'Please enter phone number' }]}
                  >
                    <Input placeholder="+852 1234 5678" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[{ type: 'email', message: 'Please enter valid email', required: false }]}
                  >
                    <Input placeholder="store@medcarehub.com" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="website" label="Website">
                    <Input placeholder="https://store.example.com" />
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Location" key="location">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={['location', 'coordinates', 0]}
                    label="Longitude"
                    rules={[{ required: true, message: 'Please enter longitude' }]}
                  >
                    <InputNumber
                      placeholder="114.1694"
                      step={0.000001}
                      precision={6}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={['location', 'coordinates', 1]}
                    label="Latitude"
                    rules={[{ required: true, message: 'Please enter latitude' }]}
                  >
                    <InputNumber
                      placeholder="22.3193"
                      step={0.000001}
                      precision={6}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name={['district', 'en']} label="District (English)">
                    <Input placeholder="Central, Tsim Sha Tsui, etc." />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name={['district', 'zh']} label="District (Chinese)">
                    <Input placeholder="中環、尖沙咀等" />
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Operating Hours" key="hours">
              {daysOfWeek.map(day => (
                <Row key={day} gutter={16} className="mb-4">
                  <Col span={4}>
                    <div className="pt-2 font-medium capitalize">{day}</div>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['operatingHours', day, 'open']} label="Open">
                      <TimePicker 
                        format="HH:mm" 
                        style={{ width: '100%' }} 
                        placeholder="Select open time"
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name={['operatingHours', day, 'close']} label="Close">
                      <TimePicker 
                        format="HH:mm" 
                        style={{ width: '100%' }} 
                        placeholder="Select close time"
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name={['operatingHours', day, 'isClosed']}
                      valuePropName="checked"
                      label="Closed"
                    >
                      <Checkbox>Closed on {day}</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              ))}
            </Tabs.TabPane>

            <Tabs.TabPane tab="Settings" key="settings">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="isActive" label="Active" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="sortOrder" label="Sort Order">
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name={['description', 'en']} label="Description (English)">
                    <TextArea rows={4} placeholder="Store description in English" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name={['description', 'zh']} label="Description (Chinese)">
                    <TextArea rows={4} placeholder="中文店鋪描述" />
                  </Form.Item>
                </Col>
              </Row>

              <Title level={5}>Manager Information</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name={['manager', 'name']} label="Manager Name">
                    <Input placeholder="Manager full name" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['manager', 'phone']} label="Manager Phone">
                    <Input placeholder="Manager phone number" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={['manager', 'email']} label="Manager Email">
                    <Input placeholder="manager@email.com" />
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>
          </Tabs>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminStores;
